/**
 * Серверы: заполняются из READY, обновляются событиями шлюза (GUILD_*, CHANNEL_*,
 * GUILD_ROLE_*, GUILD_MEMBER_*). Участники грузятся при открытии сервера.
 */
import { create } from 'zustand'
import { guildApi, type ChannelPatch, type GuildTemplate, type RolePatch, type SettingsPatch, type ThreadList, type ThreadPatch } from '@/lib/guildApi'
import type { MoveItem } from '@/lib/channelOrder'
import { DM_SETTINGS } from '@/lib/muting'
import { isEphemeral } from '@/lib/interactions'
import { useSocialStore } from './social'
import { usePresenceStore } from './presence'
import { ChannelType, type Channel, type Guild, type Invite, type Member, type MemberListItem, type MemberListUpdate, type Message, type Role, type SoundboardSound, type ThreadMember, type UserGuildSettings, type VoiceState } from '@/types/api'

/** Состояние ленивого списка участников сервера: строки по индексам плоского списка. */
export interface MemberListState {
  id: string
  member_count: number
  online_count: number
  groups: MemberListUpdate['groups']
  items: Record<number, MemberListItem>
}

interface GuildState {
  guilds: Record<string, Guild>
  channels: Record<string, Record<string, Channel>> // guildId → channelId → канал
  members: Record<string, Record<string, Member>> // guildId → userId → участник
  membersLoaded: Record<string, boolean>
  settings: Record<string, UserGuildSettings> // guildId → уведомления; DM_SETTINGS (@me) — личные беседы
  memberLists: Record<string, MemberListState> // guildId → ленивый список (op 14)
  voiceStates: Record<string, Record<string, VoiceState>> // guildId → userId → кто в каком голосовом канале

  applyReady: (guilds: Guild[], settings?: UserGuildSettings[]) => void
  /** guildId = DM_SETTINGS — переопределения личных бесед (PATCH /users/@me/guilds/@me/settings). */
  updateSettings: (guildId: string, patch: SettingsPatch) => Promise<void>
  applyEvent: (t: string, d: unknown) => void
  reset: () => void

  createGuild: (name: string, template?: GuildTemplate) => Promise<string>
  joinByCode: (code: string) => Promise<Invite>
  leaveGuild: (guildId: string) => Promise<void>
  deleteGuild: (guildId: string) => Promise<void>
  updateGuild: (guildId: string, patch: Parameters<typeof guildApi.update>[1]) => Promise<void>
  loadMembers: (guildId: string) => Promise<void>
  /** Дозапросить участников, которых нет в кэше (люди в голосе, когда список справа не открыт). */
  ensureMembers: (guildId: string, userIds: string[]) => Promise<void>
  createChannel: (guildId: string, input: { name: string; type: number; parent_id?: string | null; topic?: string }) => Promise<Channel>
  updateChannel: (channelId: string, patch: ChannelPatch) => Promise<Channel>
  deleteChannel: (channelId: string) => Promise<void>
  /** Перетаскивание: новые позиции и родители сразу применяются локально, при ошибке список перечитывается. */
  reorderChannels: (guildId: string, items: MoveItem[]) => Promise<void>
  createThread: (channelId: string, messageId: string | null, input: { name: string; auto_archive_duration?: number }) => Promise<Channel>
  updateThread: (threadId: string, patch: ThreadPatch) => Promise<void>
  joinThread: (guildId: string, threadId: string) => Promise<void>
  leaveThread: (guildId: string, threadId: string) => Promise<void>
  /** Архивные ветки канала; попадают в карту каналов, чтобы их можно было открыть. */
  loadArchivedThreads: (guildId: string, channelId: string, before?: string) => Promise<ThreadList>
  createRole: (guildId: string, input: RolePatch) => Promise<Role>
  updateRole: (guildId: string, roleId: string, patch: RolePatch) => Promise<Role>
  deleteRole: (guildId: string, roleId: string) => Promise<void>
  reorderRoles: (guildId: string, items: { id: string; position: number }[]) => Promise<void>
  updateMember: (guildId: string, userId: string, patch: Parameters<typeof guildApi.updateMember>[2]) => Promise<void>
  /**
   * Выдать (on) или снять одну роль: PUT/DELETE, чужие изменения ролей участника не затираются.
   * known — участник, если его нет в сторе (взят с сервера отдельно).
   */
  setMemberRole: (guildId: string, userId: string, roleId: string, on: boolean, known?: Member) => Promise<void>
  kick: (guildId: string, userId: string) => Promise<void>
  ban: (guildId: string, userId: string, reason?: string) => Promise<void>
}

function ingest(g: Guild): { guild: Guild; channels: Record<string, Channel>; members: Record<string, Member>; voice: Record<string, VoiceState> } {
  const { channels, threads, members, presences, voice_states, ...rest } = g
  const chans: Record<string, Channel> = {}
  for (const ch of [...(channels ?? []), ...(threads ?? [])]) chans[ch.id] = ch
  const mems: Record<string, Member> = {}
  for (const m of members ?? []) mems[m.user.id] = m
  const voice: Record<string, VoiceState> = {}
  for (const vs of voice_states ?? []) if (vs.channel_id) voice[vs.user_id] = { ...vs, guild_id: g.id }
  if (presences?.length) {
    useSocialStore.getState().setPresences(presences)
    usePresenceStore.getState().ingest(presences)
  }
  return { guild: rest, channels: chans, members: mems, voice }
}

// общий список участников, который сейчас грузится, и кого уже спрашивали по одному (ensureMembers)
const membersInFlight = new Map<string, Promise<void>>()
const memberAsked = new Set<string>()

export const useGuildStore = create<GuildState>((set, get) => ({
  guilds: {},
  channels: {},
  members: {},
  membersLoaded: {},
  settings: {},
  memberLists: {},
  voiceStates: {},

  applyReady: (list, settingsList) => {
    const guilds: Record<string, Guild> = {}
    const channels: Record<string, Record<string, Channel>> = {}
    const members: Record<string, Record<string, Member>> = {}
    const settings: Record<string, UserGuildSettings> = {}
    const voiceStates: Record<string, Record<string, VoiceState>> = {}
    for (const g of list ?? []) {
      const r = ingest(g)
      guilds[g.id] = r.guild
      channels[g.id] = r.channels
      members[g.id] = r.members
      voiceStates[g.id] = r.voice
    }
    for (const s of settingsList ?? []) settings[s.guild_id ?? DM_SETTINGS] = s
    set({ guilds, channels, members, membersLoaded: {}, settings, voiceStates })
  },

  updateSettings: async (guildId, patch) => {
    const userId = useSocialStore.getState().currentUserId
    const s = await guildApi.updateSettings(guildId, patch)
    if (useSocialStore.getState().currentUserId !== userId) return
    set((st) => ({ settings: { ...st.settings, [guildId]: s } }))
  },

  reset: () => set({ guilds: {}, channels: {}, members: {}, membersLoaded: {}, settings: {}, memberLists: {}, voiceStates: {} }),

  applyEvent: (t, d) => {
    switch (t) {
      case 'GUILD_CREATE': {
        const r = ingest(d as Guild)
        set((s) => ({
          guilds: { ...s.guilds, [r.guild.id]: r.guild },
          channels: { ...s.channels, [r.guild.id]: r.channels },
          members: { ...s.members, [r.guild.id]: { ...(s.members[r.guild.id] ?? {}), ...r.members } },
          voiceStates: { ...s.voiceStates, [r.guild.id]: r.voice },
        }))
        break
      }
      case 'GUILD_UPDATE': {
        const g = d as Guild
        set((s) => ({ guilds: { ...s.guilds, [g.id]: { ...(s.guilds[g.id] ?? {}), ...g } } }))
        break
      }
      case 'GUILD_EMOJIS_UPDATE': {
        const { guild_id, emojis } = d as { guild_id: string; emojis: Guild['emojis'] }
        set((s) => (s.guilds[guild_id] ? { guilds: { ...s.guilds, [guild_id]: { ...s.guilds[guild_id]!, emojis: emojis ?? [] } } } : {}))
        break
      }
      case 'GUILD_STICKERS_UPDATE': {
        const { guild_id, stickers } = d as { guild_id: string; stickers: Guild['stickers'] }
        set((s) => (s.guilds[guild_id] ? { guilds: { ...s.guilds, [guild_id]: { ...s.guilds[guild_id]!, stickers: stickers ?? [] } } } : {}))
        break
      }
      case 'GUILD_SOUNDBOARD_SOUND_CREATE':
      case 'GUILD_SOUNDBOARD_SOUND_UPDATE': {
        const sound = d as SoundboardSound
        const gid = sound.guild_id
        if (!gid) break
        set((s) => {
          const g = s.guilds[gid]
          if (!g) return {}
          const list = g.soundboard_sounds ?? []
          const next = list.some((x) => x.sound_id === sound.sound_id) ? list.map((x) => (x.sound_id === sound.sound_id ? sound : x)) : [...list, sound]
          return { guilds: { ...s.guilds, [gid]: { ...g, soundboard_sounds: next } } }
        })
        break
      }
      case 'GUILD_SOUNDBOARD_SOUND_DELETE': {
        const { guild_id, sound_id } = d as { guild_id: string; sound_id: string }
        set((s) => {
          const g = s.guilds[guild_id]
          if (!g) return {}
          return { guilds: { ...s.guilds, [guild_id]: { ...g, soundboard_sounds: (g.soundboard_sounds ?? []).filter((x) => x.sound_id !== sound_id) } } }
        })
        break
      }
      case 'GUILD_DELETE': {
        const { id } = d as { id: string }
        set((s) => {
          const guilds = { ...s.guilds }
          const channels = { ...s.channels }
          const members = { ...s.members }
          const membersLoaded = { ...s.membersLoaded }
          delete guilds[id]
          delete channels[id]
          delete members[id]
          delete membersLoaded[id]
          return { guilds, channels, members, membersLoaded }
        })
        break
      }
      case 'CHANNEL_CREATE':
      case 'CHANNEL_UPDATE': {
        const ch = d as Channel
        if (!ch.guild_id) break
        const gid = ch.guild_id
        set((s) => ({ channels: { ...s.channels, [gid]: { ...(s.channels[gid] ?? {}), [ch.id]: ch } } }))
        break
      }
      case 'THREAD_CREATE':
      case 'THREAD_UPDATE': {
        const ch = d as Channel
        if (!ch.guild_id) break
        const gid = ch.guild_id
        // событие без поля member не должно стирать своё участие
        set((s) => {
          const next = { ...(s.channels[gid] ?? {}), [ch.id]: { ...(s.channels[gid]?.[ch.id] ?? {}), ...ch } }
          const parent = ch.parent_id ? next[ch.parent_id] : undefined
          if (t === 'THREAD_CREATE' && parent?.type === ChannelType.GuildForum && (!parent.last_message_id || BigInt(parent.last_message_id) < BigInt(ch.id))) next[parent.id] = { ...parent, last_message_id: ch.id }
          return { channels: { ...s.channels, [gid]: next } }
        })
        break
      }
      case 'THREAD_MEMBERS_UPDATE': {
        const u = d as { id: string; guild_id: string; member_count: number; added_members: ThreadMember[]; removed_member_ids: string[] }
        const me = useSocialStore.getState().currentUserId
        set((s) => {
          const ch = s.channels[u.guild_id]?.[u.id]
          if (!ch) return {}
          const added = u.added_members.find((m) => m.user_id === me)
          const next: Channel = { ...ch, member_count: u.member_count }
          if (added) next.member = added
          if (me && u.removed_member_ids.includes(me)) next.member = null
          return { channels: { ...s.channels, [u.guild_id]: { ...s.channels[u.guild_id], [u.id]: next } } }
        })
        break
      }
      case 'MESSAGE_CREATE':
      case 'MESSAGE_UPDATE': {
        const msg = d as Message
        // «только вам» в историю не попадает: канал от него непрочитанным не становится
        if (!msg.guild_id || isEphemeral(msg)) break
        const gid = msg.guild_id
        set((s) => {
          const list = s.channels[gid]
          if (!list) return {}
          const next = { ...list }
          const ch = next[msg.channel_id]
          if (ch && t === 'MESSAGE_CREATE') {
            next[msg.channel_id] = { ...ch, last_message_id: msg.id, ...(ch.type === ChannelType.PublicThread ? { message_count: (ch.message_count ?? 0) + 1 } : {}) }
          }
          if (msg.thread) next[msg.thread.id] = { ...(next[msg.thread.id] ?? {}), ...msg.thread }
          return { channels: { ...s.channels, [gid]: next } }
        })
        break
      }
      case 'MESSAGE_DELETE_BULK': {
        const event = d as { guild_id: string; channel_id: string; ids: string[]; last_message_id: string | null }
        set((s) => {
          const ch = s.channels[event.guild_id]?.[event.channel_id]
          // Более позднее MESSAGE_CREATE не откатываем старым ответом удаления.
          if (!ch?.last_message_id || !event.ids.includes(ch.last_message_id)) return {}
          return { channels: { ...s.channels, [event.guild_id]: { ...s.channels[event.guild_id], [ch.id]: { ...ch, last_message_id: event.last_message_id } } } }
        })
        break
      }
      case 'THREAD_DELETE':
      case 'CHANNEL_DELETE': {
        const ch = d as Channel & { parent_last_message_id?: string | null }
        if (!ch.guild_id) break
        const gid = ch.guild_id
        set((s) => {
          const list = { ...(s.channels[gid] ?? {}) }
          const parentId = ch.parent_id ?? list[ch.id]?.parent_id
          const parent = parentId ? list[parentId] : undefined
          if (parent?.type === ChannelType.GuildForum && parent.last_message_id === ch.id && 'parent_last_message_id' in ch) list[parent.id] = { ...parent, last_message_id: ch.parent_last_message_id ?? null }
          delete list[ch.id]
          const voice = { ...(s.voiceStates[gid] ?? {}) }
          for (const [uid, vs] of Object.entries(voice)) if (vs.channel_id === ch.id) delete voice[uid]
          return { channels: { ...s.channels, [gid]: list }, voiceStates: { ...s.voiceStates, [gid]: voice } }
        })
        break
      }
      case 'GUILD_ROLE_CREATE':
      case 'GUILD_ROLE_UPDATE': {
        const { guild_id, role } = d as { guild_id: string; role: Role }
        set((s) => {
          const g = s.guilds[guild_id]
          if (!g) return {}
          const roles = g.roles.some((r) => r.id === role.id) ? g.roles.map((r) => (r.id === role.id ? role : r)) : [...g.roles, role]
          roles.sort((a, b) => a.position - b.position)
          return { guilds: { ...s.guilds, [guild_id]: { ...g, roles } } }
        })
        break
      }
      case 'GUILD_ROLE_DELETE': {
        const { guild_id, role_id } = d as { guild_id: string; role_id: string }
        set((s) => {
          const g = s.guilds[guild_id]
          if (!g) return {}
          const members = { ...(s.members[guild_id] ?? {}) }
          for (const [uid, m] of Object.entries(members)) {
            if (m.roles.includes(role_id)) members[uid] = { ...m, roles: m.roles.filter((r) => r !== role_id) }
          }
          return { guilds: { ...s.guilds, [guild_id]: { ...g, roles: g.roles.filter((r) => r.id !== role_id) } }, members: { ...s.members, [guild_id]: members } }
        })
        break
      }
      case 'GUILD_MEMBER_ADD':
      case 'GUILD_MEMBER_UPDATE': {
        const m = d as Member
        const gid = m.guild_id
        if (!gid) break
        set((s) => {
          const g = s.guilds[gid]
          const members = { ...s.members, [gid]: { ...(s.members[gid] ?? {}), [m.user.id]: m } }
          if (t === 'GUILD_MEMBER_ADD' && g && g.member_count !== undefined && !s.members[gid]?.[m.user.id]) {
            return { members, guilds: { ...s.guilds, [gid]: { ...g, member_count: g.member_count + 1 } } }
          }
          return { members }
        })
        break
      }
      case 'VOICE_STATE_UPDATE': {
        const vs = d as VoiceState
        if (!vs.guild_id) break
        const gid = vs.guild_id
        set((s) => {
          const list = { ...(s.voiceStates[gid] ?? {}) }
          if (vs.channel_id) list[vs.user_id] = vs
          else delete list[vs.user_id]
          const next: Partial<GuildState> = { voiceStates: { ...s.voiceStates, [gid]: list } }
          if (vs.member) next.members = { ...s.members, [gid]: { ...(s.members[gid] ?? {}), [vs.user_id]: vs.member } }
          return next
        })
        break
      }
      case 'GUILD_MEMBER_LIST_UPDATE': {
        const u = d as MemberListUpdate
        set((s) => {
          const prev = s.memberLists[u.guild_id]
          const items: Record<number, MemberListItem> = prev && prev.id === u.id ? { ...prev.items } : {}
          const members = { ...(s.members[u.guild_id] ?? {}) }
          for (const op of u.ops) {
            if (op.op !== 'SYNC') continue
            op.items.forEach((it, i) => {
              items[op.range[0] + i] = it
              if (it.member) members[it.member.user.id] = it.member
            })
          }
          return {
            memberLists: { ...s.memberLists, [u.guild_id]: { id: u.id, member_count: u.member_count, online_count: u.online_count, groups: u.groups, items } },
            members: { ...s.members, [u.guild_id]: members },
          }
        })
        break
      }
      case 'USER_GUILD_SETTINGS_UPDATE': {
        const s = d as UserGuildSettings
        set((st) => ({ settings: { ...st.settings, [s.guild_id ?? DM_SETTINGS]: s } }))
        break
      }
      case 'GUILD_MEMBER_REMOVE': {
        const { guild_id, user } = d as { guild_id: string; user: { id: string } }
        set((s) => {
          const list = { ...(s.members[guild_id] ?? {}) }
          const existed = Boolean(list[user.id])
          delete list[user.id]
          const g = s.guilds[guild_id]
          if (g && existed && g.member_count !== undefined) {
            return { members: { ...s.members, [guild_id]: list }, guilds: { ...s.guilds, [guild_id]: { ...g, member_count: Math.max(0, g.member_count - 1) } } }
          }
          return { members: { ...s.members, [guild_id]: list } }
        })
        break
      }
      default:
        break
    }
  },

  createGuild: async (name, template) => {
    const g = await guildApi.create(template ? { name, template } : { name })
    const r = ingest(g)
    set((s) => ({ guilds: { ...s.guilds, [g.id]: r.guild }, channels: { ...s.channels, [g.id]: r.channels }, members: { ...s.members, [g.id]: r.members } }))
    return g.id
  },

  joinByCode: async (code) => {
    const inv = await guildApi.acceptInvite(code)
    if (!get().guilds[inv.guild.id]) {
      // GUILD_CREATE придёт по шлюзу; на всякий случай подтягиваем сразу
      try {
        const g = await guildApi.get(inv.guild.id)
        const channels = await guildApi.channels(inv.guild.id)
        const r = ingest({ ...g, channels })
        set((s) => ({ guilds: { ...s.guilds, [g.id]: r.guild }, channels: { ...s.channels, [g.id]: r.channels } }))
      } catch {
        /* появится с событием */
      }
    }
    return inv
  },

  leaveGuild: async (guildId) => {
    await guildApi.leave(guildId)
    get().applyEvent('GUILD_DELETE', { id: guildId })
  },

  deleteGuild: async (guildId) => {
    await guildApi.remove(guildId)
    get().applyEvent('GUILD_DELETE', { id: guildId })
  },

  updateGuild: async (guildId, patch) => {
    const g = await guildApi.update(guildId, patch)
    get().applyEvent('GUILD_UPDATE', g)
  },

  loadMembers: (guildId) => {
    // уже грузится — тот же запрос: ensureMembers ждёт его, а не качает список второй раз
    if (get().membersLoaded[guildId]) return membersInFlight.get(guildId) ?? Promise.resolve()
    set((s) => ({ membersLoaded: { ...s.membersLoaded, [guildId]: true } }))
    const load = (async () => {
      try {
        const list = await guildApi.members(guildId, 1000)
        set((s) => {
          const members = { ...(s.members[guildId] ?? {}) }
          for (const m of list) members[m.user.id] = m
          return { members: { ...s.members, [guildId]: members } }
        })
      } catch {
        set((s) => ({ membersLoaded: { ...s.membersLoaded, [guildId]: false } }))
      } finally {
        membersInFlight.delete(guildId)
      }
    })()
    membersInFlight.set(guildId, load)
    return load
  },

  /*
   * Кого нечем назвать — дозапросить. Без списка участников справа (на телефоне он скрыт)
   * людей в голосе было не из чего назвать, и вместо ников стояли id (правка №22). Сначала
   * общий список, как у списка справа; кого в нём нет (сервер больше тысячи) — по одному,
   * не больше двадцати за раз и каждого не чаще одного раза.
   */
  ensureMembers: async (guildId, userIds) => {
    const missing = () => userIds.filter((id) => !get().members[guildId]?.[id])
    if (missing().length === 0) return
    await get().loadMembers(guildId)
    for (const id of missing().slice(0, 20)) {
      const key = `${guildId}:${id}`
      if (memberAsked.has(key)) continue
      memberAsked.add(key)
      try {
        const m = await guildApi.member(guildId, id)
        get().applyEvent('GUILD_MEMBER_UPDATE', { ...m, guild_id: guildId })
      } catch {
        // ушёл с сервера или удалил учётную запись — останется как есть
      }
    }
  },

  createChannel: async (guildId, input) => {
    const ch = await guildApi.createChannel(guildId, input)
    get().applyEvent('CHANNEL_CREATE', ch)
    return ch
  },

  updateChannel: async (channelId, patch) => {
    const ch = await guildApi.updateChannel(channelId, patch)
    get().applyEvent('CHANNEL_UPDATE', ch)
    return ch
  },

  deleteChannel: async (channelId) => {
    const ch = await guildApi.deleteChannel(channelId)
    get().applyEvent('CHANNEL_DELETE', ch)
  },

  reorderChannels: async (guildId, items) => {
    set((s) => {
      const list = { ...(s.channels[guildId] ?? {}) }
      for (const it of items) {
        const ch = list[it.id]
        if (ch) list[it.id] = { ...ch, position: it.position, ...(it.parent_id !== undefined ? { parent_id: it.parent_id } : {}) }
      }
      return { channels: { ...s.channels, [guildId]: list } }
    })
    try {
      await guildApi.reorderChannels(guildId, items)
    } catch (err) {
      const fresh = await guildApi.channels(guildId).catch(() => null)
      if (fresh) set((s) => ({ channels: { ...s.channels, [guildId]: Object.fromEntries(fresh.map((c) => [c.id, c])) } }))
      throw err
    }
  },

  createThread: async (channelId, messageId, input) => {
    const th = await guildApi.createThread(channelId, messageId, input)
    get().applyEvent('THREAD_CREATE', th)
    return th
  },

  updateThread: async (threadId, patch) => {
    const th = await guildApi.updateThread(threadId, patch)
    get().applyEvent('THREAD_UPDATE', th)
  },

  joinThread: async (guildId, threadId) => {
    const me = useSocialStore.getState().currentUserId ?? ''
    await guildApi.joinThread(threadId)
    if (useSocialStore.getState().currentUserId !== me) return
    set((s) => {
      const ch = s.channels[guildId]?.[threadId]
      if (!ch) return {}
      return { channels: { ...s.channels, [guildId]: { ...s.channels[guildId], [threadId]: { ...ch, member: { id: threadId, user_id: me, join_timestamp: new Date().toISOString(), flags: 0 } } } } }
    })
  },

  leaveThread: async (guildId, threadId) => {
    const me = useSocialStore.getState().currentUserId
    await guildApi.leaveThread(threadId)
    if (useSocialStore.getState().currentUserId !== me) return
    set((s) => {
      const ch = s.channels[guildId]?.[threadId]
      if (!ch) return {}
      return { channels: { ...s.channels, [guildId]: { ...s.channels[guildId], [threadId]: { ...ch, member: null } } } }
    })
  },

  loadArchivedThreads: async (guildId, channelId, before) => {
    const res = await guildApi.archivedThreads(channelId, before)
    set((s) => {
      const list = { ...(s.channels[guildId] ?? {}) }
      for (const th of res.threads) list[th.id] = { ...(list[th.id] ?? {}), ...th }
      return { channels: { ...s.channels, [guildId]: list } }
    })
    return res
  },

  createRole: async (guildId, input) => {
    const role = await guildApi.createRole(guildId, input)
    get().applyEvent('GUILD_ROLE_CREATE', { guild_id: guildId, role })
    return role
  },

  updateRole: async (guildId, roleId, patch) => {
    const role = await guildApi.updateRole(guildId, roleId, patch)
    get().applyEvent('GUILD_ROLE_UPDATE', { guild_id: guildId, role })
    return role
  },

  deleteRole: async (guildId, roleId) => {
    await guildApi.deleteRole(guildId, roleId)
    get().applyEvent('GUILD_ROLE_DELETE', { guild_id: guildId, role_id: roleId })
  },

  reorderRoles: async (guildId, items) => {
    const roles = await guildApi.reorderRoles(guildId, items)
    set((s) => {
      const g = s.guilds[guildId]
      return g ? { guilds: { ...s.guilds, [guildId]: { ...g, roles } } } : {}
    })
  },

  updateMember: async (guildId, userId, patch) => {
    const m = await guildApi.updateMember(guildId, userId, patch)
    get().applyEvent('GUILD_MEMBER_UPDATE', { ...m, guild_id: guildId })
  },

  setMemberRole: async (guildId, userId, roleId, on, known) => {
    if (on) await guildApi.addRole(guildId, userId, roleId)
    else await guildApi.removeRole(guildId, userId, roleId)
    // ответ без тела: галочку ставим сразу, не дожидаясь GUILD_MEMBER_UPDATE по шлюзу
    const m = get().members[guildId]?.[userId] ?? known
    if (!m) return
    const roles = on ? [...m.roles.filter((r) => r !== roleId), roleId] : m.roles.filter((r) => r !== roleId)
    get().applyEvent('GUILD_MEMBER_UPDATE', { ...m, roles, guild_id: guildId })
  },

  kick: async (guildId, userId) => {
    await guildApi.kick(guildId, userId)
    get().applyEvent('GUILD_MEMBER_REMOVE', { guild_id: guildId, user: { id: userId } })
  },

  ban: async (guildId, userId, reason) => {
    await guildApi.ban(guildId, userId, reason)
    get().applyEvent('GUILD_MEMBER_REMOVE', { guild_id: guildId, user: { id: userId } })
  },
}))

/** Каналы сервера, отсортированные как в списке: без категории, затем категории с детьми. */
export function sortedChannels(list: Channel[]): Channel[] {
  return [...list].sort((a, b) => (a.position ?? 0) - (b.position ?? 0) || (a.id < b.id ? -1 : 1))
}

// правила mute живут в lib/muting.ts: там их проверяют тесты без стора
export { channelMuted, guildMuted } from '@/lib/muting'
